import React, { useEffect, useMemo, useState } from "react";
import { TrendingUp, FileSpreadsheet, BarChart3, Target, Layers } from "lucide-react";
import { C, eur, pct0, pct1 } from "../igualdad-data.js";
import { apiGet, downloadCsv } from "../igualdad-api.js";
import { useIgualdad } from "../igualdad-store.jsx";
import { Status, SectionHead } from "../igualdad-components.jsx";
import { IgualdadChart } from "../igualdad-charts.jsx";

/* ============================================================
   Talention — Módulo Igualdad · Analítica
   Estructura salarial (scatter + cotas de banda) y People Analytics
   (KPIs, cuartiles, ranking de brecha y mapa de calor).
   ============================================================ */

function IgualdadEstructura() {
  const { session, fireToast } = useIgualdad();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  async function load() {
    if (!session.politicaId) { setLoading(false); setData(null); return; }
    setLoading(true); setError(null);
    try {
      const res = await apiGet(`/politicas-retributivas/${session.politicaId}/estructura`);
      setData(res.data);
    } catch (e) { setError(e.message || true); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [session.politicaId]);

  const bands = useMemo(() => (data?.bands || []).slice().sort((a, b) => a.pts - b.pts), [data]);
  const ptsM = useMemo(() => (data?.scatter?.ptsM || []).map((p) => [p.pts, Math.round(p.fijo_anual)]), [data]);
  const ptsH = useMemo(() => (data?.scatter?.ptsH || []).map((p) => [p.pts, Math.round(p.fijo_anual)]), [data]);
  const pos = useMemo(() => data?.posicion || { en: 0, encima: 0, debajo: 0, total: 0 }, [data]);
  const tot = useMemo(() => pos.total || 1, [pos]);

  function exportCsv() {
    const all = [...(data?.scatter?.ptsH || []), ...(data?.scatter?.ptsM || [])];
    downloadCsv(
      "estructura-salarial.csv",
      ["Persona", "Puntos VPT", "Retribución fija", "Posición banda %"],
      all.map((p) => [p.nombre, p.pts, Math.round(p.fijo_anual), Math.round(p.pos_banda)])
    );
    fireToast(`Exportadas ${all.length} posiciones a CSV.`);
  }

  const option = useMemo(() => ({
    legend: { top: 4, data: ["Mujeres", "Hombres"], icon: "circle", itemWidth: 9, itemHeight: 9, textStyle: { fontSize: 12, color: C.charcoal } },
    grid: { left: 56, right: 22, top: 38, bottom: 42 },
    tooltip: {
      trigger: "item", borderColor: C.border, borderWidth: 1, backgroundColor: C.surface, extraCssText: "border-radius:8px;",
      formatter: (p) =>
        `<div style="font-weight:700;font-variant-numeric:tabular-nums">${p.value[1] ? eur(p.value[1]) : ""}</div>` +
        `<div style="color:${C.textMuted}">${p.value[0]} puntos VPT</div>`,
    },
    xAxis: {
      type: "value",
      name: "Puntos de valoración (VPT)", nameLocation: "middle", nameGap: 28, nameTextStyle: { fontSize: 12, color: C.textMuted },
      axisLine: { lineStyle: { color: C.border } }, axisTick: { show: false },
      axisLabel: { fontSize: 12, color: C.textMuted }, splitLine: { lineStyle: { color: C.border } },
    },
    yAxis: {
      type: "value",
      name: "Retribución fija (€)", nameLocation: "middle", nameGap: 42, nameTextStyle: { fontSize: 12, color: C.textMuted },
      axisLine: { show: false }, axisTick: { show: false },
      axisLabel: { fontSize: 12, color: C.textMuted, formatter: (v) => `${(v / 1000).toFixed(0)}k` },
      splitLine: { lineStyle: { color: C.border } },
    },
    // ECharts no lee custom properties: C.brandLight replica --brand-300.
    series: [
      { name: "Banda superior", type: "line", data: bands.map((b) => [b.pts, Math.round(b.cotaSup)]), showSymbol: false, silent: true, lineStyle: { color: C.brandLight, width: 1.5 }, tooltip: { show: false } },
      { name: "Banda media", type: "line", data: bands.map((b) => [b.pts, Math.round(b.cotaMed)]), showSymbol: false, silent: true, lineStyle: { color: C.charcoal, width: 2 }, tooltip: { show: false } },
      { name: "Banda inferior", type: "line", data: bands.map((b) => [b.pts, Math.round(b.cotaInf)]), showSymbol: false, silent: true, lineStyle: { color: C.brand, width: 1.5 }, tooltip: { show: false } },
      { name: "Hombres", type: "scatter", data: ptsH, symbolSize: 7, itemStyle: { color: C.H, opacity: 0.78 } },
      { name: "Mujeres", type: "scatter", data: ptsM, symbolSize: 7, itemStyle: { color: C.M, opacity: 0.85 } },
    ],
  }), [bands, ptsM, ptsH]);

  return (
    <div className="rb-page">
      <h1 className="rb-h1">Estructura salarial · Política {session.politica?.ejercicio}</h1>
      {loading || error
        ? <Status loading={loading} error={error} />
        : (
          <div className="rb-estr">
            <div className="rb-card rb-estr-side">
              <div className="rb-estr-panel">
                <div className="rb-estr-panel-h" style={{ background: C.brand }}>Posición en banda · Retr. fija</div>
                <table className="rb-table rb-table-mini">
                  <tbody>
                    <tr><td className="rb-l">Por encima</td><td className="rb-mono">{pos.encima}</td><td className="rb-mono rb-soft">{pct1((pos.encima / tot) * 100)}</td></tr>
                    <tr><td className="rb-l">En banda</td><td className="rb-mono">{pos.en}</td><td className="rb-mono rb-soft">{pct1((pos.en / tot) * 100)}</td></tr>
                    <tr><td className="rb-l">Por debajo</td><td className="rb-mono">{pos.debajo}</td><td className="rb-mono rb-soft">{pct1((pos.debajo / tot) * 100)}</td></tr>
                  </tbody>
                </table>
              </div>
              <div className="rb-estr-cota">
                <div className="rb-card-h2">Cotas de la banda</div>
                <div className="rb-cotarow"><span>Cota superior · 120 %</span><b style={{ color: C.brand }}>×1,20</b></div>
                <div className="rb-cotarow"><span>Cota media · 100 %</span><b style={{ color: C.charcoal }}>×1,00</b></div>
                <div className="rb-cotarow"><span>Cota inferior · 80 %</span><b style={{ color: C.brand }}>×0,80</b></div>
                <p className="rb-note">Banda construida sobre la regresión salario–valoración de la política. Cada punto es una persona.</p>
              </div>
            </div>

            <div className="rb-card rb-estr-main">
              <SectionHead
                icon={TrendingUp}
                title="Retribución fija vs. puntos de valoración"
                right={<button className="rb-btn-ghost" onClick={exportCsv}><FileSpreadsheet size={14} /> Excel</button>}
              />
              <IgualdadChart option={option} style={{ height: 420 }} />
              <div className="rb-bandlegend">
                <span><i style={{ background: "var(--brand-300)" }} /> Banda superior (120 %)</span>
                <span><i style={{ background: C.charcoal }} /> Banda media (100 %)</span>
                <span><i style={{ background: C.brand }} /> Banda inferior (80 %)</span>
              </div>
            </div>
          </div>
        )}
    </div>
  );
}

function heatBg(b) {
  if (b === null || b === undefined) return "var(--gray-100)";
  const a = Math.abs(b);
  if (a >= 20) return "var(--brand-active)";
  if (a >= 12) return "var(--brand)";
  if (a >= 6) return "var(--brand-400)";
  if (a >= 3) return "var(--brand-200)";
  return "var(--orange-100)";
}
function heatTextOn(b) { return b !== null && Math.abs(b) >= 12 ? "var(--white)" : "var(--text-heading)"; }

function IgualdadAnalytics() {
  const { session } = useIgualdad();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [dash, setDash] = useState(null);
  const [posicion, setPosicion] = useState(null);

  async function load() {
    if (!session.politicaId) { setLoading(false); return; }
    setLoading(true); setError(null);
    try {
      const pid = session.politicaId;
      const [a, d, e] = await Promise.all([
        apiGet(`/politicas-retributivas/${pid}/analytics`),
        apiGet("/dashboard", { politica_id: pid }),
        apiGet(`/politicas-retributivas/${pid}/estructura`),
      ]);
      setAnalytics(a.data);
      setDash(d.data);
      setPosicion(e.data.posicion);
    } catch (err) { setError(err.message || true); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [session.politicaId]);

  const g = useMemo(() => dash?.brecha_global || {}, [dash]);
  const counts = useMemo(() => dash?.counts || {}, [dash]);
  const personas = useMemo(() => counts.personas || 0, [counts]);
  const enBandaPct = useMemo(() => {
    const p = posicion; if (!p || !p.total) return 0;
    return (p.en / p.total) * 100;
  }, [posicion]);
  const cuartiles = useMemo(() => analytics?.cuartiles || [], [analytics]);
  const heatmap = useMemo(() => analytics?.heatmap || [], [analytics]);
  const ranking = useMemo(() => (analytics?.ranking || [])
    .filter((r) => (r.nM + r.nH) >= 4)
    .slice(0, 7), [analytics]);
  const maxRank = useMemo(() => Math.max(...ranking.map((r) => Math.abs(r.brecha)), 1), [ranking]);

  const cuartilBar = useMemo(() => ({
    grid: { left: 38, right: 8, top: 30, bottom: 24 },
    legend: { top: 0, icon: "circle", itemWidth: 9, itemHeight: 9, textStyle: { fontSize: 12, color: C.charcoal } },
    tooltip: { trigger: "axis", backgroundColor: C.surface, borderColor: C.border, borderWidth: 1, extraCssText: "border-radius:8px;", valueFormatter: (v) => `${v} %` },
    xAxis: { type: "category", data: cuartiles.map((d) => d.label), axisLabel: { fontSize: 12, color: C.textMuted }, axisLine: { lineStyle: { color: C.border } }, axisTick: { show: false } },
    yAxis: { type: "value", axisLine: { show: false }, axisTick: { show: false }, axisLabel: { fontSize: 12, color: C.textMuted, formatter: (v) => `${v}%` }, splitLine: { lineStyle: { color: C.border } } },
    series: [
      { name: "Mujeres", type: "bar", stack: "a", data: cuartiles.map((d) => Math.round(d.pM)), itemStyle: { color: C.M } },
      { name: "Hombres", type: "bar", stack: "a", data: cuartiles.map((d) => Math.round(d.pH)), itemStyle: { color: C.H, borderRadius: [3, 3, 0, 0] } },
    ],
  }), [cuartiles]);

  return (
    <div className="rb-page">
      <h1 className="rb-h1">People Analytics</h1>
      <p className="rb-lead" style={{ marginBottom: 18 }}>Lectura analítica de la plantilla: distribución, segregación y dónde se concentra la brecha.</p>

      {loading || error
        ? <Status loading={loading} error={error} />
        : (
          <>
            <div className="rb-kpis">
              <div className="rb-kpi"><div className="rb-kpi-l">Plantilla</div><div className="rb-kpi-v">{personas}</div><div className="rb-kpi-s">{Math.round(g.pM || 0)} % mujeres</div></div>
              <div className="rb-kpi"><div className="rb-kpi-l">Brecha media</div><div className="rb-kpi-v" style={{ color: C.brand }}>{pct1(g.brechaMedia || 0)}</div><div className="rb-kpi-s">retribución total</div></div>
              <div className="rb-kpi"><div className="rb-kpi-l">Brecha mediana</div><div className="rb-kpi-v">{pct1(g.brechaMediana || 0)}</div><div className="rb-kpi-s">total</div></div>
              <div className="rb-kpi"><div className="rb-kpi-l">En banda salarial</div><div className="rb-kpi-v">{pct0(enBandaPct)}</div><div className="rb-kpi-s">posición 80–120 %</div></div>
            </div>

            <div className="rb-grid2-2">
              <div className="rb-card">
                <SectionHead icon={BarChart3} title="Distribución por cuartiles salariales" />
                <IgualdadChart option={cuartilBar} style={{ height: 250 }} />
                <div className="rb-note">El cuartil superior (Q4) concentra menos mujeres → segregación vertical.</div>
              </div>

              <div className="rb-card">
                <SectionHead icon={Target} title="Puestos con mayor brecha" />
                <div className="rb-rank">
                  {ranking.map((r) => (
                    <div key={r.label} className="rb-rank-row">
                      <div className="rb-rank-lbl">{r.label}<span className="rb-soft rb-xs"> · {r.nM + r.nH}</span></div>
                      <div className="rb-rank-bar"><div style={{ width: (Math.abs(r.brecha) / maxRank) * 100 + "%", background: r.brecha >= 0 ? C.H : C.M }} /></div>
                      <div className="rb-rank-val" style={{ color: r.brecha >= 0 ? C.H : C.M }}>{pct1(r.brecha)}</div>
                    </div>
                  ))}
                  {!ranking.length && <div className="rb-soft rb-xs">Sin puestos con suficiente plantilla para comparar.</div>}
                </div>
              </div>
            </div>

            <div className="rb-card">
              <SectionHead icon={Layers} title="Mapa de calor de brechas por grupo y concepto" />
              <table className="rb-table rb-heat">
                <thead><tr><th className="rb-l">Grupo profesional</th><th>Brecha fija</th><th>Brecha variable</th><th>Brecha total</th></tr></thead>
                <tbody>
                  {heatmap.map((row) => (
                    <tr key={row.label}>
                      <td className="rb-l rb-strong">{row.label}</td>
                      {["fijo", "variable", "total"].map((key) => (
                        <td className="rb-c" key={key}>
                          <span className="rb-heatcell" style={{ background: heatBg(row.conceptos[key].brecha), color: heatTextOn(row.conceptos[key].brecha) }}>{pct1(row.conceptos[key].brecha)}</span>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="rb-heatkey"><span>Menor</span><i style={{ background: "var(--orange-100)" }} /><i style={{ background: "var(--brand-200)" }} /><i style={{ background: "var(--brand-400)" }} /><i style={{ background: "var(--brand)" }} /><i style={{ background: "var(--brand-active)" }} /><span>Mayor brecha</span></div>
            </div>
          </>
        )}
    </div>
  );
}

export { IgualdadEstructura, IgualdadAnalytics };
